import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * React 18 does not hoist <title>, <meta> or <link> elements rendered inside
 * page components into <head>. Pages in this project render those tags in JSX,
 * so without this component every route would inherit the static index.html
 * title, description and canonical — a duplicate-metadata problem across the
 * whole site.
 *
 * HeadSync mirrors page-level metadata found in <body> into <head> after each
 * render. Originals are left in place (React still owns them); the copies are
 * tagged with data-head-sync so they can be replaced on the next navigation.
 */

const MIRRORED_META = new Set([
  "description",
  "robots",
  "author",
  "keywords",
]);

function clearPrevious() {
  document.head
    .querySelectorAll("[data-head-sync]")
    .forEach((el) => el.remove());
}

function upsert(el: Element) {
  const clone = el.cloneNode(true) as Element;
  clone.setAttribute("data-head-sync", "true");
  document.head.appendChild(clone);
}

function removeStatic(selector: string) {
  document.head
    .querySelectorAll(`${selector}:not([data-head-sync])`)
    .forEach((el) => el.remove());
}

function sync() {
  const bodyTitle = document.body.querySelector("title");
  const bodyMetas = Array.from(document.body.querySelectorAll("meta[name], meta[property]"));
  const bodyLinks = Array.from(
    document.body.querySelectorAll('link[rel="canonical"], link[rel="alternate"]')
  );

  if (!bodyTitle && bodyMetas.length === 0 && bodyLinks.length === 0) return;

  clearPrevious();

  if (bodyTitle?.textContent) {
    document.title = bodyTitle.textContent;
  }

  const seen = new Set<string>();

  bodyMetas.forEach((meta) => {
    const name = meta.getAttribute("name");
    const property = meta.getAttribute("property");
    const key = name ? `name:${name}` : property ? `property:${property}` : "";
    if (!key || seen.has(key)) return;
    if (name && !MIRRORED_META.has(name) && !name.startsWith("twitter:")) return;
    seen.add(key);
    // The page-level tag wins over the static fallback in index.html.
    if (name) removeStatic(`meta[name="${name}"]`);
    if (property) removeStatic(`meta[property="${property}"]`);
    upsert(meta);
  });

  bodyLinks.forEach((link) => {
    const rel = link.getAttribute("rel");
    const hrefLang = link.getAttribute("hreflang");
    const key = `${rel}:${hrefLang ?? ""}:${link.getAttribute("href")}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (rel === "canonical") removeStatic('link[rel="canonical"]');
    upsert(link);
  });
}

export default function HeadSync() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Run after the route's own render, and once more when lazy content lands.
    sync();
    const t = window.setTimeout(sync, 300);
    const observer = new MutationObserver(() => sync());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.clearTimeout(t);
      observer.disconnect();
    };
  }, [pathname]);

  return null;
}
